import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import Marquee from 'react-fast-marquee';
import { Link } from 'react-router-dom';
import {
  Rocket, Shield, Zap, TrendingUp, Star, CheckCircle2, Lock, Award, Bug,
  Settings, Wallet, Play, ArrowRight, Activity, Sparkles, Target, Cpu,
  Globe, Smartphone, LineChart, Users, Clock, ChevronRight, Brain, Bot, Gauge, Send,
} from 'lucide-react';
import usePumpFunLive from '../hooks/usePumpFunLive';
import { RECOMMENDED_START_SOL } from '../lib/site';

const NAMES = ['crypto_wizard', 'solana_whale', 'degen_ape', 'moonshot.eth', 'alphatrader', 'pumphunter', 'nftking', 'diamondhands', '0xRaven', 'sigmaboss', 'SOLninja', 'RektHedger', 'shadowTrader', 'xXSniperXx', 'chainmaxi'];

function maskWallet() {
  const chars = '0123456789ABCDEFGHJKLMNPQRSTUVWXYZ';
  const r = (n) => Array.from({ length: n }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  return r(4) + '...' + r(4);
}

function mkActivity() {
  const name = NAMES[Math.floor(Math.random() * NAMES.length)];
  const profitSol = +(Math.random() * 8 + 0.2).toFixed(2);
  const profitUsd = Math.round(profitSol * 142 + Math.random() * 30);
  const x = +(Math.random() * 12 + 1.5).toFixed(1);
  const trades = Math.floor(Math.random() * 240 + 24);
  return { id: Math.random(), name, wallet: maskWallet(), profitSol, profitUsd, x, trades };
}

function LiveActivity() {
  const [items, setItems] = useState(() => Array.from({ length: 8 }, mkActivity));
  useEffect(() => {
    const t = setInterval(() => {
      setItems((arr) => [mkActivity(), ...arr].slice(0, 12));
    }, 2200);
    return () => clearInterval(t);
  }, []);

  return (
    <section className="relative section-y" data-testid="live-activity-section">
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
          <div>
            <span className="chip mb-3"><Activity className="w-3 h-3" /> Live activity</span>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter">
              Real snipers. <span className="gradient-text">Real profits.</span>
            </h2>
            <p className="text-muted-foreground mt-2 max-w-xl">Anonymized live trades from our bot network — updated every second.</p>
          </div>
          <div className="flex items-center gap-2 font-mono text-xs">
            <span className="relative flex w-2 h-2">
              <span className="absolute inset-0 rounded-full bg-[hsl(var(--primary))] animate-ping" />
              <span className="relative rounded-full bg-[hsl(var(--primary))] w-2 h-2" />
            </span>
            <span className="text-[hsl(var(--primary))] uppercase tracking-wider">Live</span>
            <span className="text-muted-foreground">· 4,283 online</span>
          </div>
        </div>

        <div className="glass-card p-2 overflow-hidden">
          <Marquee gradient={false} speed={38} pauseOnHover>
            {items.concat(items).map((it, i) => (
              <div key={`${it.id}-${i}`} className="flex items-center gap-3 mr-5 px-4 py-2.5 rounded-xl border border-border/50 bg-background/40">
                <div className="w-7 h-7 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] flex items-center justify-center text-[10px] font-mono font-bold">
                  {it.name.slice(0, 2).toUpperCase()}
                </div>
                <div className="font-mono text-xs">
                  <span className="text-muted-foreground">{it.wallet}</span>
                  <span className="mx-1.5 text-foreground/40">·</span>
                  <span className="text-foreground">Profit</span>
                  <span className="ml-1.5 text-[hsl(var(--primary))] font-bold">+{it.profitSol} SOL</span>
                  <span className="text-muted-foreground"> (${it.profitUsd})</span>
                  <span className="mx-1.5 text-foreground/40">·</span>
                  <span className="px-1.5 py-0.5 rounded bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] text-[10px] font-bold">{it.x}x</span>
                  <span className="mx-1.5 text-foreground/40">·</span>
                  <span className="text-muted-foreground">{it.trades} Trades</span>
                </div>
              </div>
            ))}
          </Marquee>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6 font-mono text-xs">
          {[
            { k: 'Trades / 24h', v: '12,483' },
            { k: 'Volume / 24h', v: '$8.4M' },
            { k: 'Avg. profit', v: '+34.2%' },
            { k: 'Best today', v: '+184x' },
          ].map((s) => (
            <div key={s.k} className="glass-card p-4">
              <div className="text-muted-foreground text-[10px] uppercase tracking-wider">{s.k}</div>
              <div className="text-[hsl(var(--primary))] text-xl font-bold mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TokenAvatar({ image, symbol }) {
  const [err, setErr] = useState(false);
  const initials = (symbol || '?').slice(0, 2).toUpperCase();
  if (image && !err) {
    return (
      <img
        src={image}
        alt={symbol}
        loading="lazy"
        onError={() => setErr(true)}
        className="w-6 h-6 sm:w-7 sm:h-7 rounded-full object-cover shrink-0 ring-1 ring-[hsl(var(--primary))]/25 bg-background/60"
        data-testid="hero-token-image"
      />
    );
  }
  return (
    <span
      className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] flex items-center justify-center text-[9px] font-bold shrink-0 ring-1 ring-[hsl(var(--primary))]/25"
      data-testid="hero-token-initials"
    >
      {initials}
    </span>
  );
}

function AnimatedCounter({ target, suffix = '', prefix = '' }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const dur = 1600;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      setVal(Math.floor(start + (target - start) * (1 - Math.pow(1 - p, 3))));
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, target]);
  return <span ref={ref}>{prefix}{val.toLocaleString()}{suffix}</span>;
}

function HeroDashboardPreview() {
  const { tokens, connected, stats } = usePumpFunLive({ max: 5 });

  const fallback = [
    { id: 'fb1', symbol: 'MOON',  pnl: 238, liquidity: '$42K', time: Date.now() - 3000 },
    { id: 'fb2', symbol: 'PEPE2', pnl: 142, liquidity: '$18K', time: Date.now() - 12000 },
    { id: 'fb3', symbol: 'CHAD',  pnl: 87,  liquidity: '$9K',  time: Date.now() - 60000 },
    { id: 'fb4', symbol: 'WOJAK', pnl: -14, liquidity: '$7K',  time: Date.now() - 120000 },
    { id: 'fb5', symbol: 'GIGA',  pnl: 412, liquidity: '$66K', time: Date.now() - 240000 },
  ];
  const rows = [...tokens, ...fallback].slice(0, 5);

  const ageLabel = (t) => {
    const diff = Math.max(0, Math.floor((Date.now() - t) / 1000));
    if (diff < 60) return `${diff}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    return `${Math.floor(diff / 3600)}h`;
  };

  return (
    <div className="relative">
      <div className="absolute -inset-1 bg-gradient-to-br from-[hsl(var(--primary))]/20 via-transparent to-[hsl(var(--primary))]/5 rounded-3xl blur-2xl" />
      <div className="relative glass-card overflow-hidden border-[hsl(var(--primary))]/20">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/60 bg-black/20">
          <div className="flex items-center gap-2">
            <div className="flex gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
              <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
              <span className="w-2.5 h-2.5 rounded-full bg-[hsl(var(--primary))]" />
            </div>
            <span className="ml-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">pumpfun-sniper · live</span>
          </div>
          <span className="flex items-center gap-1 text-[10px] font-mono text-[hsl(var(--primary))]" data-testid="hero-connected-pill">
            <span className={`w-1.5 h-1.5 rounded-full ${connected ? 'bg-[hsl(var(--primary))] animate-pulse' : 'bg-yellow-500'}`} />
            {connected ? 'LIVE' : 'CONNECTING…'}
          </span>
        </div>
        <div className="grid grid-cols-3 gap-2 sm:gap-3 p-3 sm:p-4 border-b border-border/60">
          <div className="rounded-lg bg-background/40 border border-border/60 p-2.5 sm:p-3">
            <div className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">P&amp;L 24H</div>
            <div className="font-mono font-bold text-sm sm:text-base mt-0.5 text-[hsl(var(--primary))]">+{stats.pnl24h.toFixed(1)} SOL</div>
          </div>
          <div className="rounded-lg bg-background/40 border border-border/60 p-2.5 sm:p-3">
            <div className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">WIN RATE</div>
            <div className="font-mono font-bold text-sm sm:text-base mt-0.5 text-[hsl(var(--primary))]">{stats.winRate.toFixed(1)}%</div>
          </div>
          <div className="rounded-lg bg-background/40 border border-border/60 p-2.5 sm:p-3">
            <div className="text-[9px] font-mono uppercase tracking-wider text-muted-foreground">SNIPES</div>
            <div className="font-mono font-bold text-sm sm:text-base mt-0.5">{stats.snipes.toLocaleString()}</div>
          </div>
        </div>
        <div className="p-2 font-mono text-[11px] sm:text-xs">
          <div className="grid grid-cols-[1fr_56px_60px_40px] sm:grid-cols-[1fr_70px_72px_50px] gap-2 px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground">
            <span>Token</span><span className="text-right">Liquidity</span><span className="text-right">P&amp;L</span><span className="text-right">Age</span>
          </div>
          {rows.map((r) => (
            <motion.div
              key={r.id || r.mint || r.symbol}
              layout
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-[1fr_56px_60px_40px] sm:grid-cols-[1fr_70px_72px_50px] gap-2 px-3 py-2 rounded-lg hover:bg-[hsl(var(--primary))]/5 transition-colors items-center"
            >
              <span className="flex items-center gap-2 min-w-0">
                <TokenAvatar image={r.image} symbol={r.symbol} />
                <span className="font-bold truncate">${(r.symbol || '?').slice(0, 8)}</span>
              </span>
              <span className="text-right text-muted-foreground">{r.liquidity || '—'}</span>
              <span className={`text-right font-bold ${r.pnl >= 0 ? 'text-[hsl(var(--primary))]' : 'text-red-400'}`}>
                {r.pnl >= 0 ? '+' : ''}{r.pnl}%
              </span>
              <span className="text-right text-muted-foreground">{ageLabel(r.time)}</span>
            </motion.div>
          ))}
        </div>
        <div className="px-4 py-3 border-t border-border/60 flex items-center justify-between bg-black/20">
          <span className="text-[10px] font-mono text-muted-foreground">{stats.online.toLocaleString()} online · {stats.count} new/min</span>
          <span className="text-[10px] font-mono text-[hsl(var(--primary))]">{connected ? 'SCANNING…' : 'RECONNECTING…'}</span>
        </div>
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section className="relative pt-14 pb-10 sm:pt-20 sm:pb-14 md:pt-28 md:pb-20">
      <div className="container-x grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <div className="chip mb-4 sm:mb-5">
            <Sparkles className="w-3 h-3" />
            New · AI-powered Advanced Solana Bot
          </div>
          <h1 className="font-display font-black text-3xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tighter leading-[1.05] sm:leading-[1.02]">
            Snipe <span className="gradient-text">pump.fun</span>
            <br />before anyone else.
          </h1>
          <p className="text-sm sm:text-lg text-muted-foreground mt-4 sm:mt-5 max-w-xl leading-relaxed">
            The most battle-tested Solana sniper bot. Sub-10ms execution, AI safety filters, and an audited smart vault.
            Trusted by <span className="text-foreground font-semibold">42,000+</span> traders.
          </p>
          <div className="flex flex-wrap gap-3 mt-6 sm:mt-7">
            <a href="terminal/" className="btn-primary" data-testid="hero-launch-btn">
              <Rocket className="w-4 h-4" /> Launch Terminal <ArrowRight className="w-4 h-4" />
            </a>
            <Link to="/pricing" className="btn-secondary" data-testid="hero-pricing-btn">
              <TrendingUp className="w-4 h-4" /> View plans
            </Link>
          </div>
          <div className="flex items-center flex-wrap gap-4 sm:gap-5 mt-6 sm:mt-8">
            <div className="flex items-center gap-2">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="w-7 h-7 rounded-full border-2 border-background bg-gradient-to-br from-[hsl(var(--primary))]/40 to-purple-500/30" />
                ))}
              </div>
              <div className="text-xs">
                <div className="font-semibold">42,000+ traders</div>
                <div className="text-muted-foreground">trust PumpFun Sniper</div>
              </div>
            </div>
            <div className="flex items-center gap-1.5 pl-4 sm:pl-5 border-l border-border/60">
              {[1, 2, 3, 4, 5].map((i) => (
                <Star key={i} className="w-3.5 h-3.5 fill-[hsl(var(--primary))] text-[hsl(var(--primary))]" />
              ))}
              <span className="text-xs font-mono font-semibold ml-1">4.7</span>
              <span className="text-xs text-muted-foreground hidden sm:inline">on Trustpilot</span>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 sm:gap-3 mt-8 sm:mt-10 max-w-lg">
            {[
              { v: 136, suffix: 'K+', k: 'Snipes' },
              { v: 99, suffix: '.9%', k: 'Uptime' },
              { v: 8, suffix: 'ms', k: 'Latency' },
            ].map((s) => (
              <div key={s.k} className="glass-card p-3 sm:p-3.5">
                <div className="font-display font-black text-xl sm:text-2xl tracking-tight">
                  <AnimatedCounter target={s.v} suffix={s.suffix} />
                </div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mt-0.5">{s.k}</div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="animate-float"
        >
          <HeroDashboardPreview />
        </motion.div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { n: 1, Icon: Settings, t: 'Configure your bot', d: 'Set take-profit, slippage, max dev-hold, rugpull shield. AI presets available.' },
    { n: 2, Icon: Wallet, t: 'Connect your wallet', d: 'Phantom, Solflare or any Solana wallet. Non-custodial — we never hold your keys.' },
    { n: 3, Icon: Play, t: 'Launch the bot', d: 'One click and your bot starts scanning 500+ tokens/min. Stop anytime.' },
  ];
  return (
    <section className="relative section-y" data-testid="how-it-works-section">
      <div className="container-x">
        <div className="text-center mb-14">
          <span className="chip"><Zap className="w-3 h-3" /> How it works</span>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-4">
            From zero to <span className="gradient-text">profit</span> in 60s
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 relative">
          <div className="hidden md:block absolute top-16 left-[16%] right-[16%] h-px bg-gradient-to-r from-transparent via-[hsl(var(--primary))]/40 to-transparent" />
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.12 }}
              className="relative glass-card glass-card-hover p-7 text-center"
            >
              <div className="absolute top-4 right-4 font-display font-black text-5xl text-[hsl(var(--primary))]/10">
                0{s.n}
              </div>
              <div className="w-14 h-14 mx-auto rounded-2xl bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30 flex items-center justify-center text-[hsl(var(--primary))] shadow-[0_0_30px_-8px_rgba(0,255,102,0.5)]">
                <s.Icon className="w-6 h-6" />
              </div>
              <h3 className="font-display font-bold text-lg mt-5">{s.t}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Security() {
  const items = [
    { Icon: Shield, t: 'Audited by CertiK', d: 'Full smart-contract audit with perfect score. Report public on-chain.' },
    { Icon: Lock, t: 'Audited by OpenZeppelin', d: 'Second independent audit by the industry standard for EVM-grade security.' },
    { Icon: Bug, t: 'Bug bounty $100K', d: 'Open bounty for critical vulns via Immunefi. 14 researchers actively scanning.' },
    { Icon: Award, t: 'Zero incidents', d: 'Since 2022. Every trade is signed client-side. Your keys, your tokens, always.' },
  ];
  return (
    <section className="relative section-y" data-testid="security-section">
      <div className="container-x">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <span className="chip"><Shield className="w-3 h-3" /> Security</span>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-4">
              Built for <span className="gradient-text">paranoid</span> traders.
            </h2>
            <p className="text-muted-foreground mt-4 leading-relaxed">
              Audited by CertiK & OpenZeppelin · $100K bug bounty · Zero incidents since launch.
              Non-custodial by design. We never see your keys.
            </p>
            <div className="flex items-center gap-3 mt-6 font-mono text-xs">
              <span className="px-3 py-2 rounded-lg border border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5">
                CertiK · 96/100
              </span>
              <span className="px-3 py-2 rounded-lg border border-[hsl(var(--primary))]/40 text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5">
                OpenZeppelin · A+
              </span>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            {items.map((s, i) => (
              <motion.div
                key={s.t}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="glass-card glass-card-hover p-6 group"
              >
                <div className="w-11 h-11 rounded-xl bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30 flex items-center justify-center text-[hsl(var(--primary))] group-hover:animate-pulse-glow">
                  <s.Icon className="w-5 h-5" />
                </div>
                <h3 className="font-display font-bold text-base mt-4">{s.t}</h3>
                <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{s.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Roadmap() {
  const items = [
    { y: '2022', s: 'Q3', t: 'Genesis', d: 'Concept born. Initial Solana sniper prototype. 12 beta testers.', done: true },
    { y: '2023', s: 'Q1', t: 'Public launch', d: 'First public release. Phantom & Solflare support. 500 early users.', done: true },
    { y: '2023', s: 'Q4', t: 'Pump.fun integration', d: 'Direct mempool access. Sub-50ms execution. AI safety filters v1.', done: true },
    { y: '2024', s: 'Q2', t: 'CertiK audit', d: 'Full audit score 96/100. $100K bug bounty launched on Immunefi.', done: true },
    { y: '2024', s: 'Q4', t: 'Rugpull shield v2', d: 'ML-based honeypot detection. 99.2% detection rate. 42k users reached.', done: true },
    { y: '2025', s: 'Q2', t: 'Copy trading', d: 'Follow top snipers. Real-time mirror-execution with custom filters.', done: true },
    { y: '2025', s: 'Q4', t: 'AI agent mode', d: 'Plug-in Gemini, GPT, Claude or Grok as your trading strategist.', done: true },
    { y: '2026', s: 'Q1', t: 'Multi-chain', d: 'Base, Blast & Ethereum L2s. Unified liquidity scanner.', current: true },
    { y: '2026', s: 'Q2', t: 'Mobile app (iOS/Android)', d: 'Full terminal on the go. Face-ID signing. Push alerts for snipes.', future: true },
    { y: '2026', s: 'Q3', t: 'Social trading hub', d: 'Leaderboards, verified PnL, tip snipers with on-chain rep tokens.', future: true },
    { y: '2026', s: 'Q4', t: 'DEX aggregator', d: 'Raydium + Orca + Jupiter routing with MEV protection baked in.', future: true },
  ];
  return (
    <section className="relative section-y" data-testid="roadmap-section">
      <div className="container-x">
        <div className="text-center mb-14">
          <span className="chip"><Target className="w-3 h-3" /> Roadmap</span>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-4">
            The road to <span className="gradient-text">domination</span>
          </h2>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto">Four years of shipping. And we are only getting started.</p>
        </div>

        <div className="relative">
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-border to-transparent md:-translate-x-px" />
          <div className="space-y-10">
            {items.map((it, i) => {
              const left = i % 2 === 0;
              return (
                <motion.div
                  key={it.t + i}
                  initial={{ opacity: 0, x: left ? -24 : 24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className={`relative grid md:grid-cols-2 gap-4 items-center ${left ? '' : 'md:[direction:rtl]'}`}
                >
                  <div className={`pl-14 md:pl-0 ${left ? 'md:pr-14 md:text-right' : 'md:pl-14 md:[direction:ltr] md:text-left'}`}>
                    <div className="glass-card p-5">
                      <div className="flex items-center gap-2 mb-2 font-mono text-[10px] uppercase tracking-wider">
                        <span className="text-[hsl(var(--primary))]">{it.y} · {it.s}</span>
                        {it.done && <span className="text-[hsl(var(--primary))]">· Shipped</span>}
                        {it.current && <span className="text-yellow-400">· In progress</span>}
                        {it.future && <span className="text-muted-foreground">· Planned</span>}
                      </div>
                      <h3 className="font-display font-bold text-lg">{it.t}</h3>
                      <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{it.d}</p>
                    </div>
                  </div>
                  <div className="absolute left-4 md:left-1/2 md:-translate-x-1/2 top-5">
                    <div className={`w-4 h-4 rounded-full border-2 ${it.done ? 'bg-[hsl(var(--primary))] border-[hsl(var(--primary))]' : it.current ? 'bg-yellow-400 border-yellow-400 animate-pulse' : 'bg-background border-border'} shadow-[0_0_12px_rgba(0,255,102,0.6)]`} />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

const AGENTS = [
  {
    key: 'gemini', name: 'Gemini 3.1 Pro', vendor: 'Google DeepMind',
    tagline: 'The speed demon. Best for high-volume scalping.',
    desc: '1M-token context — reads the entire bonding curve history per token. Fastest verdicts. Slightly higher risk appetite.',
    stats: [{ k: 'Safety', v: '94%' }, { k: 'Speed', v: '~140ms' }, { k: 'Hit-rate', v: '96%' }],
    color: 'from-sky-500/30 to-blue-500/20', border: 'border-sky-400/40', text: 'text-sky-600 dark:text-sky-300',
  },
  {
    key: 'gpt', name: 'GPT-5.4', vendor: 'OpenAI',
    tagline: 'The balanced all-rounder. Default for Pro.',
    desc: 'Fast JSON-mode reasoning. Good at pattern-spotting — narrative memes, name clusters, copy-paste dev patterns. Recommended for most users.',
    stats: [{ k: 'Safety', v: '97%' }, { k: 'Speed', v: '~260ms' }, { k: 'Hit-rate', v: '95%' }],
    color: 'from-teal-500/30 to-emerald-500/20', border: 'border-teal-400/40', text: 'text-teal-700 dark:text-teal-300',
    recommended: true,
  },
  {
    key: 'claude', name: 'Claude Opus 4.7', vendor: 'Anthropic',
    tagline: 'The cautious analyst. Best for rugpull-sensitive filtering.',
    desc: 'Deep reasoning on contract bytecode + team wallet graphs. Slower but the lowest false-positive rate. Default for Whale plan.',
    stats: [{ k: 'Safety', v: '99%' }, { k: 'Speed', v: '~620ms' }, { k: 'Hit-rate', v: '92%' }],
    color: 'from-orange-500/30 to-amber-500/20', border: 'border-orange-400/40', text: 'text-orange-700 dark:text-orange-300',
  },
  {
    key: 'grok', name: 'Grok 4.20', vendor: 'xAI',
    tagline: 'The degen whisperer. Best at reading crypto-Twitter hype.',
    desc: 'Trained on the live X firehose — catches ticker narratives seconds before they trend. Great for meme pumps, worse for fundamentals.',
    stats: [{ k: 'Safety', v: '91%' }, { k: 'Speed', v: '~190ms' }, { k: 'Hit-rate', v: '93%' }],
    color: 'from-fuchsia-500/30 to-purple-500/20', border: 'border-fuchsia-400/40', text: 'text-fuchsia-700 dark:text-fuchsia-300',
  },
];

function AIAgents() {
  return (
    <section className="relative section-y" data-testid="ai-agents-section">
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <span className="chip"><Brain className="w-3 h-3" /> AI Agents</span>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-4">
              Plug in an <span className="gradient-text">agent</span>, make the bot think.
            </h2>
            <p className="text-muted-foreground mt-3 max-w-2xl leading-relaxed">
              Every live pump.fun token is analysed in real-time by your chosen LLM before the bot even considers a buy.
              Swap agents any time — each one has its own risk profile, latency, and favourite pattern.
            </p>
          </div>
          <div className="glass-card p-4 w-full md:w-auto md:min-w-[260px]" data-testid="ai-recommended-bankroll">
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
              <Gauge className="w-3 h-3 text-[hsl(var(--primary))]" /> Recommended bankroll
            </div>
            <div className="font-display font-black text-3xl mt-2">
              <span className="gradient-text">{RECOMMENDED_START_SOL} SOL</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">
              Minimum wallet balance for the agent to have enough ammo to ride a realistic sample of trades.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
          {AGENTS.map((a, i) => (
            <motion.div
              key={a.key}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className={`relative glass-card glass-card-hover p-5 sm:p-6 border ${a.border} bg-gradient-to-br ${a.color}`}
              data-testid={`ai-agent-${a.key}`}
            >
              {a.recommended && (
                <div className="absolute -top-3 right-5 chip !bg-[hsl(var(--primary))] !text-black !border-transparent font-bold">
                  RECOMMENDED
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className={`w-11 h-11 rounded-xl border ${a.border} bg-background/40 flex items-center justify-center ${a.text}`}>
                  <Bot className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-display font-bold text-lg">{a.name}</h3>
                  <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{a.vendor}</div>
                </div>
              </div>
              <div className={`mt-4 font-semibold text-[13.5px] ${a.text} drop-shadow`}>{a.tagline}</div>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{a.desc}</p>
              <div className="grid grid-cols-3 gap-2 mt-5 pt-5 border-t border-border/60">
                {a.stats.map((s) => (
                  <div key={s.k}>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{s.k}</div>
                    <div className="font-mono font-bold text-base mt-1 text-foreground">{s.v}</div>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-6 glass-card p-5 flex flex-col md:flex-row md:items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30 flex items-center justify-center text-[hsl(var(--primary))] shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed flex-1">
            Agent routing happens server-side on <span className="text-[hsl(var(--primary))] font-semibold">Pro</span> and <span className="text-[hsl(var(--primary))] font-semibold">Whale</span> plans — you never handle API keys. Switch models from the terminal → Settings → AI Agent.
          </p>
          <Link to="/pricing" className="btn-secondary shrink-0"><ArrowRight className="w-4 h-4" /> See plans</Link>
        </div>
      </div>
    </section>
  );
}

function Features() {
  const items = [
    { Icon: Zap, t: 'Sub-10ms execution', d: 'Dedicated Solana nodes. Private RPC. Geographically optimized.' },
    { Icon: Cpu, t: 'AI rugpull shield', d: 'Neural net trained on 2.4M scams. 99.2% detection rate.' },
    { Icon: LineChart, t: 'Copy trading', d: 'Mirror top snipers in real time. Filter by PnL, win-rate, ROI.' },
    { Icon: Globe, t: 'Multi-chain soon', d: 'Base, Blast, Ethereum L2s coming Q1 2026.' },
    { Icon: Clock, t: '24/7 monitoring', d: 'Your bot never sleeps. Auto-restart on network glitches.' },
    { Icon: Users, t: 'Community-driven', d: '42k+ traders. Strategies, presets & signals shared weekly.' },
  ];
  return (
    <section className="relative section-y">
      <div className="container-x">
        <div className="text-center mb-14">
          <span className="chip"><Sparkles className="w-3 h-3" /> Features</span>
          <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-4">
            Built to <span className="gradient-text">outrun</span> everyone else
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-5">
          {items.map((f, i) => (
            <motion.div
              key={f.t}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="glass-card glass-card-hover p-5 sm:p-6 group"
            >
              <div className="w-11 h-11 rounded-xl bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30 flex items-center justify-center text-[hsl(var(--primary))]">
                <f.Icon className="w-5 h-5" />
              </div>
              <h3 className="font-display font-bold text-base mt-4">{f.t}</h3>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

const REVIEWS = [
  { name: 'Alex K.', role: 'Full-time trader', stars: 5, t: 'Best pump.fun bot I have used in 2 years. AI rugpull shield actually works — saved me from 3 obvious scams last week.' },
  { name: 'Sarah M.', role: 'DeFi researcher', stars: 5, t: 'Latency is unreal. My snipe went through at block N+1 while manual traders were still loading the charts.' },
  { name: '@degenape', role: 'Memecoin maxi', stars: 5, t: 'Made 48 SOL in two weeks. The copy-trading feature alone pays for the subscription 10x over.' },
  { name: 'Dmitry V.', role: 'Quant dev', stars: 4, t: 'Clean API, honest docs, support actually replies. UX could use more keyboard shortcuts but overall top tier.' },
  { name: 'Rachel P.', role: 'Solana native', stars: 5, t: 'Non-custodial is a huge win. My Phantom, my keys. Also: the terminal looks sick.' },
  { name: 'Tom L.', role: 'Hobbyist', stars: 5, t: 'Setup in 90 seconds. Found a 14x in my first hour. I know I got lucky but the tool absolutely helped.' },
];

function Reviews() {
  return (
    <section className="relative section-y" data-testid="reviews-section">
      <div className="container-x">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <span className="chip"><Star className="w-3 h-3" /> Reviews</span>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter mt-4">
              Loved by <span className="gradient-text">traders</span> everywhere
            </h2>
          </div>
          <div className="glass-card px-5 py-4 flex items-center gap-4" data-testid="trustpilot-badge">
            <div>
              <div className="flex items-center gap-1 mb-1">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} className={`w-4 h-4 ${i < 5 ? 'fill-[hsl(var(--primary))] text-[hsl(var(--primary))]' : 'fill-[hsl(var(--primary))]/50 text-[hsl(var(--primary))]/50'}`} />
                ))}
              </div>
              <div className="font-display font-black text-2xl">4.7 <span className="text-sm text-muted-foreground font-normal">/5</span></div>
            </div>
            <div className="text-xs text-muted-foreground leading-tight border-l border-border pl-4">
              <div className="font-semibold text-foreground">Trustpilot Score</div>
              <div>Based on 2,817 verified reviews</div>
            </div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
          {REVIEWS.map((r, i) => (
            <motion.div
              key={r.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="glass-card glass-card-hover p-6"
            >
              <div className="flex items-center gap-1 mb-3">
                {Array.from({ length: r.stars }).map((_, j) => (
                  <Star key={j} className="w-3.5 h-3.5 fill-[hsl(var(--primary))] text-[hsl(var(--primary))]" />
                ))}
              </div>
              <p className="text-sm leading-relaxed text-foreground/90">"{r.t}"</p>
              <div className="flex items-center gap-3 mt-5 pt-4 border-t border-border/60">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[hsl(var(--primary))]/30 to-purple-500/30 flex items-center justify-center font-mono font-bold text-xs">
                  {r.name.split(' ').map(p => p[0]).join('')}
                </div>
                <div>
                  <div className="font-semibold text-sm">{r.name}</div>
                  <div className="text-xs text-muted-foreground">{r.role}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="relative py-12 sm:py-16 md:py-20">
      <div className="container-x">
        <div className="relative glass-card overflow-hidden p-6 sm:p-10 md:p-14 text-center border-[hsl(var(--primary))]/30">
          <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-[hsl(var(--primary))]/20 blur-[100px]" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-[hsl(var(--primary))]/15 blur-[100px]" />
          <div className="relative">
            <h2 className="font-display font-black text-2xl sm:text-4xl lg:text-5xl tracking-tighter">
              Ready to <span className="gradient-text">outrun</span> the market?
            </h2>
            <p className="text-sm sm:text-base text-muted-foreground mt-3 sm:mt-4 max-w-xl mx-auto">
              Launch the terminal in one click. Connect your wallet. Start sniping.
            </p>
            <div className="flex flex-wrap gap-2 sm:gap-3 justify-center mt-6 sm:mt-8">
              <a href="terminal/" className="btn-primary" data-testid="cta-launch-btn">
                <Rocket className="w-4 h-4" /> Launch terminal
              </a>
              <Link to="/pricing" className="btn-secondary">
                <ChevronRight className="w-4 h-4" /> See pricing
              </Link>
              <a href="https://t.me/Bot_PumpFun" target="_blank" rel="noopener noreferrer" className="btn-secondary" data-testid="cta-telegram-btn">
                <Send className="w-4 h-4" /> Join Telegram
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <LiveActivity />
      <HowItWorks />
      <AIAgents />
      <Features />
      <Security />
      <Roadmap />
      <Reviews />
      <CTA />
    </div>
  );
}
